import { KeyInitiative } from 'partner360/entities/keyInitiatives'
import { sumActivities } from 'partner360/features/mdfActivities/helpers/sumActivities'
import { IMdfActivity } from 'partner360/features/mdfActivities/model/types'

import {
  IMdfInitiativeGroupRow,
  IMdfInitiativeGroupTotal,
  NO_INITIATIVE_ROW_ID,
} from '../model/types'

const getCoveragePercent = (requested: number, planned: number): number | null =>
  planned > 0 ? Math.round((requested / planned) * 100) : null

/**
 * Joins key initiatives with their linked MDF activities to produce the rows
 * of the "Planned vs actual funding by initiative" table. Activities are
 * grouped onto an initiative via `activity.keyInitiativeId === initiative.EntryId`.
 * Unlinked activities roll up into a synthetic "No initiative" row. Grand
 * totals are returned separately as `total` (rendered in the table footer).
 * Returns empty rows and a `null` total when there is nothing to show.
 */
export const getMdfInitiativeGroups = (
  initiatives: KeyInitiative[],
  activities: IMdfActivity[],
  isUsd: boolean
): { rows: IMdfInitiativeGroupRow[]; total: IMdfInitiativeGroupTotal | null } => {
  const activitiesByInitiative = new Map<number, IMdfActivity[]>()
  const unlinkedActivities: IMdfActivity[] = []

  activities.forEach((activity) => {
    if (activity.keyInitiativeId === null) {
      unlinkedActivities.push(activity)
      return
    }

    const group = activitiesByInitiative.get(activity.keyInitiativeId) ?? []
    group.push(activity)
    activitiesByInitiative.set(activity.keyInitiativeId, group)
  })

  const initiativeRows: IMdfInitiativeGroupRow[] = initiatives
    .filter((initiative) => activitiesByInitiative.has(initiative.EntryId))
    .map((initiative) => {
      const group = activitiesByInitiative.get(initiative.EntryId) ?? []
      const { requested, paid } = sumActivities(group, isUsd)
      const planned = initiative.HPInvestmentSum ?? 0

      return {
        id: initiative.EntryId,
        name: initiative.NameExt ?? '',
        activityCount: group.length,
        activities: group,
        planned,
        requested,
        paid,
        coveragePercent: getCoveragePercent(requested, planned),
        rowType: 'initiative' as const,
      }
    })
    .sort((left, right) => right.planned - left.planned)

  const dataRows = [...initiativeRows]

  if (unlinkedActivities.length > 0) {
    const { requested, paid } = sumActivities(unlinkedActivities, isUsd)

    dataRows.push({
      id: NO_INITIATIVE_ROW_ID,
      name: '',
      activityCount: unlinkedActivities.length,
      activities: unlinkedActivities,
      planned: 0,
      requested,
      paid,
      coveragePercent: null,
      rowType: 'noInitiative',
    })
  }

  if (dataRows.length === 0) {
    return { rows: [], total: null }
  }

  const totals = dataRows.reduce(
    (acc, row) => {
      acc.planned += row.planned
      acc.requested += row.requested
      acc.paid += row.paid
      return acc
    },
    { planned: 0, requested: 0, paid: 0 }
  )

  return {
    rows: dataRows,
    total: {
      planned: totals.planned,
      requested: totals.requested,
      paid: totals.paid,
      coveragePercent: getCoveragePercent(totals.requested, totals.planned),
    },
  }
}
